"use client";

import { useEffect, useRef, useState } from "react";
import { AssetImage } from "@/components/optilase/asset-image";
import type { SbBlokData } from "@storyblok/react/rsc";
import { storyblokEditable } from "@/lib/storyblok-editable";

import { ClinicLink } from "@/components/clinic/clinic-link";
import { resolveBg } from "@/lib/bg-color";
import { byName, mergeRegionalPair, type Clinic } from "@/lib/clinics";
import { usePublicPathname } from "@/lib/public-pathname";

export type ClinicGridBlok = SbBlokData & {
  title?: string;
  body?: string;
  clinics?: string[];
  bg_color?: unknown;
};

type Filter = "all" | "ireland" | "uk";

const FILTERS: { id: Filter; label: string }[] = [
  { id: "all", label: "All clinics" },
  { id: "ireland", label: "Ireland" },
  { id: "uk", label: "United Kingdom" },
];

function ArrowIcon() {
  return (
    <svg width="17" height="11" viewBox="0 0 17 11" fill="none" aria-hidden>
      <path
        d="M1 5.5h14.5M11 1l4.5 4.5L11 10"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ClinicCard({
  clinic,
  cardRef,
}: {
  clinic: Clinic;
  cardRef: (el: HTMLLIElement | null) => void;
}) {
  const address = [clinic.addressLine1, clinic.addressLine2].filter(Boolean).join(", ");
  return (
    <li ref={cardRef} className="w-[82%] shrink-0 snap-start sm:w-auto">
      <ClinicLink
        clinic={clinic}
        className="group border-grey-01 flex h-full flex-col overflow-hidden rounded-2xl border bg-white"
      >
        <div className="bg-teal-02 relative aspect-[624/283] w-full overflow-hidden">
          <AssetImage
            src={clinic.photo?.filename ?? ""}
            focus={clinic.photo?.focus}
            alt={clinic.photo?.alt || clinic.name}
            fill
            sizes="(min-width: 1115px) 405px, (min-width: 640px) 50vw, 100vw"
            className="object-cover"
          />
          {clinic.flagship && (
            <span className="text-teal-08 absolute top-6 right-6 rounded-full bg-white px-4 py-1.5 text-xs font-medium tracking-wide uppercase">
              Primary
            </span>
          )}
        </div>

        <div className="flex flex-1 flex-col gap-4 p-6 lg:p-8">
          <h3 className="text-grey-09 text-lg leading-[1.2] font-bold">{clinic.name}</h3>
          {address && <p className="text-grey-06 leading-normal">{address}</p>}
          <span className="text-teal-08 mt-auto inline-flex items-center gap-2 pt-2 font-bold group-hover:underline">
            View clinic
            <ArrowIcon />
          </span>
        </div>
      </ClinicLink>
    </li>
  );
}

export default function ClinicGridView({
  blok,
  clinics,
}: {
  blok: ClinicGridBlok;
  clinics: Clinic[];
}) {
  const { bg, onDark } = resolveBg(blok.bg_color, "white");
  const [filter, setFilter] = useState<Filter>("all");
  const [activeIndex, setActiveIndex] = useState(0);
  const [isScrollable, setIsScrollable] = useState(false);
  const scrollerRef = useRef<HTMLUListElement>(null);
  const cardRefs = useRef<(HTMLLIElement | null)[]>([]);

  const hasIreland = clinics.some((c) => c.country === "Ireland");
  const hasUk = clinics.some((c) => c.country === "United Kingdom");
  const showFilters = hasIreland && hasUk;

  const pathname = usePublicPathname();
  const onUk = pathname === "/uk" || pathname.startsWith("/uk/");
  const filterOrder: Filter[] = onUk ? ["all", "uk", "ireland"] : ["all", "ireland", "uk"];
  const orderedFilters = filterOrder.map((id) => FILTERS.find((f) => f.id === id)!);

  const filtered =
    filter === "ireland"
      ? byName(clinics).filter((c) => c.country === "Ireland")
      : filter === "uk"
        ? byName(clinics).filter((c) => c.country === "United Kingdom")
        : mergeRegionalPair(byName(clinics));

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    const update = () => {
      const rect = scroller.getBoundingClientRect();
      const center = rect.left + rect.width / 2;
      let closest = 0;
      let minDist = Infinity;
      cardRefs.current.forEach((card, i) => {
        if (!card) return;
        const cRect = card.getBoundingClientRect();
        const cCenter = cRect.left + cRect.width / 2;
        const dist = Math.abs(cCenter - center);
        if (dist < minDist) {
          minDist = dist;
          closest = i;
        }
      });
      const maxScroll = scroller.scrollWidth - scroller.clientWidth;
      if (scroller.scrollLeft <= 1) closest = 0;
      else if (scroller.scrollLeft >= maxScroll - 1) closest = filtered.length - 1;
      setActiveIndex(closest);
      setIsScrollable(scroller.scrollWidth > scroller.clientWidth + 4);
    };

    scroller.scrollLeft = 0;
    update();
    scroller.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      scroller.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [filter, filtered.length]);

  const hasHeader = Boolean(blok.title || blok.body);

  return (
    <section {...storyblokEditable(blok)} className={`${bg} px-5 py-10 md:px-10 lg:px-20 lg:py-20`}>
      <div className="mx-auto w-full max-w-7xl">
        {hasHeader && (
          <div className="flex max-w-201.5 flex-col gap-5">
            {blok.title && (
              <h2 className={`text-section ${onDark ? "text-white" : "text-teal-07"}`}>
                {blok.title}
              </h2>
            )}
            {blok.body && (
              <p
                className={`text-base leading-normal lg:text-xl ${
                  onDark ? "text-white/85" : "text-grey-06"
                }`}
              >
                {blok.body}
              </p>
            )}
          </div>
        )}

        {showFilters && (
          <div
            className={`flex flex-wrap items-center gap-2 ${
              hasHeader ? "border-grey-01 mt-10 border-t pt-10" : ""
            }`}
          >
            {orderedFilters.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setFilter(f.id)}
                aria-pressed={filter === f.id}
                className={`inline-flex h-10 cursor-pointer items-center rounded-full px-6 font-medium transition-colors ${
                  filter === f.id
                    ? "bg-teal-07 text-white"
                    : "border-grey-01 text-grey-06 hover:border-teal-04 hover:text-teal-07 border bg-white"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        )}

        <ul
          ref={scrollerRef}
          className={`flex snap-x snap-mandatory [scrollbar-width:none] gap-5 overflow-x-auto [-ms-overflow-style:none] sm:grid sm:snap-none sm:grid-cols-2 sm:gap-8 sm:overflow-visible lg:grid-cols-3 [&::-webkit-scrollbar]:hidden ${
            showFilters ? "mt-8" : hasHeader ? "mt-8 lg:mt-12" : ""
          }`}
        >
          {filtered.map((clinic, i) => (
            <ClinicCard
              key={clinic.uuid}
              clinic={clinic}
              cardRef={(el) => {
                cardRefs.current[i] = el;
              }}
            />
          ))}
        </ul>

        {isScrollable && filtered.length > 1 && (
          <div className="mt-6 flex justify-center sm:hidden">
            <div className="flex items-center">
              {filtered.map((clinic, i) => (
                <button
                  key={clinic.uuid}
                  type="button"
                  onClick={() => {
                    cardRefs.current[i]?.scrollIntoView({
                      behavior: "smooth",
                      inline: "center",
                      block: "nearest",
                    });
                  }}
                  aria-label={`Go to clinic ${i + 1}`}
                  className="flex h-6 min-w-6 cursor-pointer items-center justify-center"
                >
                  <span
                    aria-hidden
                    className={`block h-1.5 rounded-full transition-all duration-300 ${
                      activeIndex === i ? "bg-teal-07 w-8" : "bg-teal-03 w-2"
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
